/**
 * Whisper STT Provider
 *
 * Speech-to-Text using OpenAI's Whisper model via Transformers.js.
 * Supports 100+ languages, MIT license.
 *
 * Uses WebGPU acceleration when available, falls back to WASM.
 */

import { STTProvider } from './STTProvider.js';
import { pipeline } from '@huggingface/transformers';

// Languages that Whisper supports well
const WHISPER_LANGUAGES = [
  'en', 'zh', 'de', 'es', 'ru', 'ko', 'fr', 'ja', 'pt', 'tr', 'pl', 'ca',
  'nl', 'ar', 'sv', 'it', 'id', 'hi', 'fi', 'vi', 'he', 'uk', 'el', 'ms',
  'cs', 'ro', 'da', 'hu', 'ta', 'no', 'th', 'ur', 'hr', 'bg', 'lt', 'la',
  'mi', 'ml', 'cy', 'sk', 'te', 'fa', 'lv', 'bn', 'sr', 'az', 'sl', 'kn',
  'et', 'mk', 'br', 'eu', 'is', 'hy', 'ne', 'mn', 'bs', 'kk', 'sq', 'sw',
  'gl', 'mr', 'pa', 'si', 'km', 'sn', 'yo', 'so', 'af', 'oc', 'ka', 'be',
  'tg', 'sd', 'gu', 'am', 'yi', 'lo', 'uz', 'fo', 'ht', 'ps', 'tk', 'nn',
  'mt', 'sa', 'lb', 'my', 'bo', 'tl', 'mg', 'as', 'tt', 'haw', 'ln', 'ha',
  'ba', 'jw', 'su',
];

export class WhisperProvider extends STTProvider {
  constructor() {
    super('whisper');
    this.transcriber = null;
    // Xenova's whisper-small - better accuracy for multilingual transcription (~240MB)
    this.model = 'Xenova/whisper-small';
    this.loading = false;
    this.loadingProgress = 0;
    this._initPromise = null; // Lock to prevent parallel initialization
  }

  /**
   * Check if Whisper is supported
   * Requires WebGPU for good performance, WASM SIMD as fallback
   */
  static isSupported(capabilities) {
    // WebGPU preferred, but WASM works too (slower)
    return capabilities.wasmSimd;
  }

  /**
   * Check if this provider supports a given language
   */
  static supportsLanguage(language) {
    return WHISPER_LANGUAGES.includes(language);
  }

  /**
   * Initialize Whisper model
   * @param {Object} options - Configuration options
   */
  async initialize(options = {}) {
    // Already initialized
    if (this.initialized) {
      return;
    }

    // Already loading - wait for current initialization
    if (this._initPromise) {
      console.log('[Whisper] Already loading, waiting for current initialization...');
      return this._initPromise;
    }

    // Start initialization with lock
    this._initPromise = this._doInitialize(options);
    try {
      await this._initPromise;
    } finally {
      this._initPromise = null;
    }
  }

  /**
   * Internal initialization method
   */
  async _doInitialize(options = {}) {
    console.log('[Whisper] Initializing...');
    this.loading = true;
    this.loadingProgress = 0;

    try {
      if (options.model) {
        this.model = options.model;
      }

      if (options.language) {
        this.language = options.language;
      }

      // Determine device (WebGPU or WASM)
      let device = 'wasm';
      let dtype = 'fp32';

      if ('gpu' in navigator) {
        try {
          const adapter = await navigator.gpu.requestAdapter();
          if (adapter) {
            device = 'webgpu';
            dtype = 'fp16'; // Use fp16 for WebGPU
            console.log('[Whisper] Using WebGPU acceleration');
          }
        } catch (e) {
          console.log('[Whisper] WebGPU not available, using WASM');
        }
      }

      // Create the transcription pipeline
      this.transcriber = await pipeline('automatic-speech-recognition', this.model, {
        device,
        dtype,
        progress_callback: (progress) => {
          if (progress.progress !== undefined) {
            this.loadingProgress = Math.round(progress.progress);
            if (this.onLoadingProgress) {
              this.onLoadingProgress(this.loadingProgress, progress.status);
            }
          }
        },
      });

      this.initialized = true;
      this.loading = false;
      this.loadingProgress = 100;
      console.log('[Whisper] Initialized successfully with model:', this.model);
    } catch (error) {
      this.loading = false;
      console.error('[Whisper] Initialization failed:', error);
      throw error;
    }
  }

  /**
   * Transcribe audio to text
   * @param {Float32Array} audio - Audio samples at 16kHz
   * @param {Object} options - Transcription options
   * @returns {Promise<{text: string, segments?: Array}>}
   */
  async transcribe(audio, options = {}) {
    if (!this.initialized) {
      throw new Error('Whisper not initialized. Call initialize() first.');
    }

    if (this.onTranscriptionStart) {
      this.onTranscriptionStart();
    }

    try {
      const startTime = performance.now();
      const audioSeconds = audio.length / 16000;

      // Prepare transcription options
      const lang = options.language || this.language || 'en';
      console.log(`[Whisper] Transcribing with language: ${lang}, audio: ${audioSeconds.toFixed(1)}s`);

      const transcribeOptions = {
        language: lang,
        task: 'transcribe',
        chunk_length_s: 30,
        stride_length_s: 5,
        return_timestamps: false,
        max_new_tokens: 448,
      };

      // Run transcription
      const result = await this.transcriber(audio, transcribeOptions);

      const endTime = performance.now();
      const duration = (endTime - startTime) / 1000;
      const rtf = duration / audioSeconds;

      console.log(`[Whisper] Transcribed ${audioSeconds.toFixed(1)}s audio in ${duration.toFixed(2)}s (RTF: ${rtf.toFixed(2)}x)`);

      if (this.onTranscriptionEnd) {
        this.onTranscriptionEnd(result);
      }

      // Normalize result format
      return {
        text: result.text || '',
        segments: result.chunks || [],
        language: this.language,
        duration: audioSeconds,
        processingTime: duration,
        rtf,
      };
    } catch (error) {
      console.error('[Whisper] Transcription failed:', error);
      throw error;
    }
  }

  /**
   * Get loading progress
   */
  getLoadingProgress() {
    return this.loadingProgress;
  }

  /**
   * Clean up resources
   */
  async destroy() {
    console.log('[Whisper] Destroying...');

    try {
      if (this.transcriber) {
        // Dispose of the pipeline
        await this.transcriber.dispose?.();
        this.transcriber = null;
      }

      this.initialized = false;
      this.loading = false;
      this.loadingProgress = 0;

      console.log('[Whisper] Destroyed successfully');
    } catch (error) {
      console.error('[Whisper] Destroy error:', error);
    }
  }
}

export default WhisperProvider;
